/**
 * In-memory WorkWise submission state for the standard New Deployment wizard:
 * one correlation id per wizard session, and the deployment already created by
 * POST /workwise/deployments/. If the trigger fails after the create, the next
 * Deploy click reuses that id (and correlation id) and only repeats the trigger,
 * so no second deployment appears. Cleared by the wizard store's reset().
 *
 * Deliberately not persisted (no localStorage).
 */
import { create } from 'zustand'
import { generateCorrelationId } from '@/utils/workwise/defaults'

export interface CreatedDeployment {
  id: number
  correlationId: string
}

interface WorkwiseDraftState {
  /** One id per wizard session; reused until the deployment is created. */
  correlationId: string | null
  created: CreatedDeployment | null

  ensureCorrelationId: () => string
  setCreated: (created: CreatedDeployment) => void
  clear: () => void
}

export const useWorkwiseDraft = create<WorkwiseDraftState>((set, get) => ({
  correlationId: null,
  created: null,

  ensureCorrelationId: () => {
    const existing = get().correlationId
    if (existing) return existing
    const fresh = generateCorrelationId()
    set({ correlationId: fresh })
    return fresh
  },

  setCreated: (created) => set({ created, correlationId: created.correlationId }),

  clear: () => set({ correlationId: null, created: null }),
}))
